import * as Tone from 'tone';

// Audio-clip Tone.Player manager. Decoupled from the data model in song.js
// so the player lifecycle (load buffer, connect to channel, dispose on
// remove) can be tested in isolation. Keys are the clip's id.

export function createAudioClipManager(ctx) {
    const players = new Map(); // clipId → { player, channelId }

    async function addClip(clipId, audioBuffer, channelId) {
        if (players.has(clipId)) await removeClip(clipId);
        const player = new Tone.Player();
        player.buffer = new Tone.ToneAudioBuffer(audioBuffer);
        const channel = ctx.mixer.get(channelId);
        if (channel) player.connect(channel.input);
        players.set(clipId, { player, channelId });
        return player;
    }

    function removeClip(clipId) {
        const entry = players.get(clipId);
        if (!entry) return;
        try { entry.player.disconnect(); entry.player.dispose(); } catch (_) {}
        players.delete(clipId);
    }

    function moveToChannel(clipId, channelId) {
        const entry = players.get(clipId);
        if (!entry) return;
        try { entry.player.disconnect(); } catch (_) {}
        const ch = ctx.mixer.get(channelId);
        if (ch) entry.player.connect(ch.input);
        entry.channelId = channelId;
    }

    function trigger(clipId, time, opts = {}) {
        const entry = players.get(clipId);
        if (!entry || !entry.player.loaded) return;
        try {
            if (entry.player.state === 'started') entry.player.stop(time);
            // Apply per-trigger fade values; Tone.Player exposes scalar
            // fadeIn / fadeOut properties (in seconds).
            if (opts.fadeIn !== undefined) entry.player.fadeIn = Math.max(0, opts.fadeIn);
            if (opts.fadeOut !== undefined) entry.player.fadeOut = Math.max(0, opts.fadeOut);
            // Tone.Player.start(time, offsetSec, durationSec).
            const offset = Math.max(0, opts.offset || 0);
            const dur = Number.isFinite(opts.duration) && opts.duration > 0 ? opts.duration : undefined;
            entry.player.start(time, offset, dur);
        } catch (_) { /* retrigger race */ }
    }

    // Returns the underlying AudioBuffer for a clip, useful for waveform
    // rendering. Tone.ToneAudioBuffer.get() exposes the raw buffer.
    function getBuffer(clipId) {
        const entry = players.get(clipId);
        const tb = entry?.player?.buffer;
        if (!tb) return null;
        try { return typeof tb.get === 'function' ? tb.get() : tb; } catch { return null; }
    }

    const peakCache = new Map(); // clipId|buckets → Float32Array of [min,max] pairs

    // Compute (or fetch from cache) min/max amplitude buckets for a clip.
    // Used by the arrangement view to draw a waveform inside each clip.
    function getPeaks(clipId, numBuckets) {
        const cacheKey = `${clipId}|${numBuckets}`;
        if (peakCache.has(cacheKey)) return peakCache.get(cacheKey);
        const buffer = getBuffer(clipId);
        if (!buffer) return null;
        const data = buffer.getChannelData(0); // mono — first channel is enough for visual peaks
        const peaks = new Float32Array(numBuckets * 2);
        const samplesPerBucket = data.length / numBuckets;
        for (let i = 0; i < numBuckets; i++) {
            const start = Math.floor(i * samplesPerBucket);
            const end = Math.min(data.length, Math.floor((i + 1) * samplesPerBucket));
            let min = 1, max = -1;
            for (let j = start; j < end; j++) {
                const s = data[j];
                if (s < min) min = s;
                if (s > max) max = s;
            }
            peaks[i * 2] = min;
            peaks[i * 2 + 1] = max;
        }
        peakCache.set(cacheKey, peaks);
        return peaks;
    }
    function clearPeakCache(clipId) {
        if (clipId) {
            for (const k of [...peakCache.keys()]) {
                if (k.startsWith(clipId + '|')) peakCache.delete(k);
            }
        } else {
            peakCache.clear();
        }
    }

    function stopClip(clipId, time) {
        const entry = players.get(clipId);
        if (!entry) return;
        try { entry.player.stop(time); } catch (_) {}
    }

    function stopAll(time) {
        for (const [, entry] of players) {
            try { entry.player.stop(time); } catch (_) {}
        }
    }

    function clear() {
        for (const id of [...players.keys()]) removeClip(id);
    }

    function has(clipId) { return players.has(clipId); }

    return { addClip, removeClip, moveToChannel, trigger, stopClip, stopAll, clear, has, getBuffer, getPeaks, clearPeakCache };
}
